<template>
<div class='column-tree-table-default-cell'>
  <custom-tooltip popper-class="light-tooltip">
    <div slot='buttonContent'>
      <div class='column-tree-table-default-cell__name' :class='{"has-inner-layer" : innerLayers > 0 && !hasLayer}'
           :style='[hasLayer? {"background-color": colorMap? getFlatColor: hasLayer.color} : ""]'>
        <a :href='data.url' target="_blank">
          {{data.name}}
        </a>
        <div v-if='hasMoreCells' class='column-tree-table-default-cell__name__counter'>{{ numberOfCells }}</div>
      </div>
    </div>
    <div slot="content">
      <slot name="tooltip">
        {{getTooltip}}
      </slot>
    </div>
  </custom-tooltip>
  <div v-if='hasMoreCells' @click.stop="showMore = !showMore" class='sidebar'>
      <div v-if='showMore' class="angle top">
        <svg width="14px" height="14px">
          <path d="M0 14H14V0Z" />
        </svg>
      </div>
      <div v-else class='placeholder' />
      <div class="handle"> = </div>
      <div v-if='showMore' class="angle bottom">
        <svg width="14px" height="14px">
          <path d="M0 0H14V14Z" />
        </svg>
      </div>
      <div v-else class='placeholder' />
    </div>
  <div v-if='hasMoreCells && showMore'>
    <column-tree-table-default-cell v-for='cell in data.cells' :key='cell.id' :data='cell' :layer='layer' :focus='focus' :color-map='colorMap' :data-version='dataVersion' :tactic="tactic"/>
  </div>
</div>
</template>

<script>
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'ColumnTreeTableDefaultCell',
  components: {CustomTooltip},
  props: {
    data: {
      required: true,
      type: Object,
    },
    layer: {
      required: false,
      type: Array,
      default: () => [],
    },
    focus: {
      required: false,
      type: String,
      default: '',
    },
    colorMap: {
      required: false,
      type: Object,
      default: () => undefined,
    },
    dataVersion: {
      required: true,
      type: Number,
    },
    tactic: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      showMore: false,
    };
  },
  computed: {
    hasMoreCells() {
      return this?.data?.cells?.length > 0;
    },
    numberOfCells() {
      return this.innerLayers > 0 ? `(${this.innerLayers}/${this?.data?.cells?.length})` : `(${this?.data?.cells?.length})`;
    },
    hasLayer() {
      return this.layer.find(obj => (obj.techniqueID === this.data.id && obj?.tactic === this.tactic));
    },
    getTooltip() {
      const layer = this.hasLayer;
      return layer ? `${this.data.id}: ${layer.comment}` : this.data.id;
    },
    innerLayers() {
      return this.layer.reduce((accumulator, currentLayer) => {
        if (currentLayer.techniqueID !== this.data.id && currentLayer.techniqueID.includes(this.data.id)) {
          if (this.colorMap) {
            if (this.dataVersion === 1) {
              const pattern = /\([^()]*\)|[^()]+/g;
              const formattedAPTs = this.focus?.match(pattern)
                ?.map(match => match.trim()
                  ?.replace(/^\(|\)$/g, ''));
              return (formattedAPTs.some(apt => currentLayer.comment.includes(apt))) ? (accumulator + 1) : accumulator;
            }
            if (this.dataVersion === 2) {
              // For v2, the layer is already filtered by color, so just count it
              return accumulator + 1;
            }
          }
          return accumulator + 1;
        }
        return accumulator;
      }, 0);
    },
    getFlatColor() {
      if (this.dataVersion === 1) {
        const pattern = /\([^()]*\)|[^()]+/g;
        const formattedAPTs = this.focus?.match(pattern)
          ?.map(match => match.trim()
            ?.replace(/^\(|\)$/g, ''));
        if (this.colorMap && formattedAPTs?.some(apt => this.getTooltip.includes(apt))) {
          return this.colorMap[this.focus];
        }
      }
      if (this.dataVersion === 2) {
        // For v2, the layer is already filtered by color in DomainInsights,
        // so if hasLayer exists, just use its color directly
        if (this.hasLayer) {
          return this.hasLayer.color;
        }
      }
      return '';
    },
  },
  methods: {
    // eslint-disable-next-line no-unused-vars
    countLayer(payload) {
      this.innerLayers += 1;
    },
  },
};
</script>

<style lang='scss' scoped>
.column-tree-table-default-cell {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: -1px;
  align-items: center;

  .has-inner-layer {
    background-color: var(--bg-tertiary);
  }

  &__name {
    padding: 5px;
    font-size: 11px;
    border: 1px solid var(--border-primary);
    background-color: var(--bg-primary);
    width: 120px;
    white-space: initial;

    a {
      color: var(--text-primary);
      text-decoration: none;

      &:hover {
        color: var(--brand-primary);
      }
    }

    &__counter {
      font-size: 8px;
      text-align: right;
    }
  }

  .sidebar {
    background: var(--text-muted);
    cursor: pointer;
    min-width: 8px;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    justify-content: space-between;

    .angle {
      background-color: var(--corner-bg-color, var(--bg-secondary));
      display: flex;

      svg {
        fill: var(--text-muted);
        vertical-align: baseline;
      }
    }

    .placeholder {
      width: 14px;
    }

    .handle {
      transform: rotate(-90deg);
      color: var(--bg-primary);
      width: 12px;
      font-size: 16px;
      margin-right: 2px;
    }
  }
}
</style>
